import { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from "react";
import { flushSync } from "react-dom";
import { useParams, useNavigate, useBlocker } from "react-router-dom";
import { Loader2, Heart, Building2, ArrowLeftRight, Users, Receipt } from "lucide-react";
import { EntityEditHeaderCard } from "@/components/ui/EntityEditHeaderCard";
import {
    EntityEditHeaderToolbar,
    type EntityEditRelatedLink,
} from "@/components/ui/EntityEditHeaderToolbar";
import { EntityEditTabBar } from "@/components/ui/EntityEditTabBar";
import { PageHeader } from "@/features/invitee/components/PageHeader";
import { CompanyInfoTab } from "../components/edit-tabs/CompanyInfoTab";
import { ExecutiveSummaryTab } from "../components/edit-tabs/ExecutiveSummaryTab";
import { ContactsTab } from "../components/edit-tabs/ContactsTab";
import { PreferencesTab } from "../components/edit-tabs/PreferencesTab";
import { CommunicationsTab } from "../components/edit-tabs/CommunicationsTab";
import { IntegrationsTab } from "../components/edit-tabs/IntegrationsTab";
import { CompanyConfigTab } from "../components/edit-tabs/CompanyConfigTab";
import { ActivityTab } from "../components/edit-tabs/ActivityTab";
import { PaymentsTab } from "../components/edit-tabs/PaymentsTab";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import {
    companyEditConfirmPresentation,
    COMPANY_EDIT_TABS,
    COMPANY_EDIT_TABS_WITHOUT_GLOBAL_FORM,
} from "../constants";
import { LibertyInternalNotes } from "../components/LibertyInternalNotes";
import { useUserRole } from "@/hooks/useUserRole";
import apiClient from "@/lib/api/apiClient";
import { showApiError, showError, showInfo, showSuccess } from "@/lib/utils/notifications";
import { sendAnalytics } from "@/lib/utils/analytics";
import { postCompanyUpdate } from "../services/companyService";
import {
    parseDuplicateCompanyAddressFromApiError,
    CompanyDuplicateAddressCancelledError,
} from "../utils/companyDuplicateAddress";
import { PROPERTYLIST_FILTER_SESSION_KEY } from "@/features/property/constants";
import type {
    CompanyEditDetails,
    CompanyEditTabId,
    DuplicateCompanyRow,
} from "../types";
import { COMPANY_SPECIFIC_ROLES } from "@/constants/roles";
import { openTransferWizardInNewTab } from "@/features/transfer";
import { useBreadcrumbPageTitle } from "@/components/layout/DashboardBreadcrumbContext";
import { shouldBlockUnsavedNavigation } from "@/lib/utils/unsavedNavigationBlock";

function getFirstAllowedCompanyEditTabId(hasPolicyFn: (keys: string[]) => boolean): CompanyEditTabId {
    const available = COMPANY_EDIT_TABS.filter((tab) => !tab.policy || hasPolicyFn([tab.policy]));
    return (available[0]?.id ?? "companyinfo") as CompanyEditTabId;
}

function getNoGlobalSubmitMessage(_tab: CompanyEditTabId): string {
    return "Use this tab's own Save button or actions where shown.";
}

function isFormControlTargetForDirtyMark(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    const form = target.closest("form[data-tab-id]");
    if (!form) return false;
    const tag = target.tagName;
    if (tag !== "INPUT" && tag !== "TEXTAREA" && tag !== "SELECT") return false;
    const el = target as HTMLInputElement;
    const type = (el.type || "").toLowerCase();
    if (type === "hidden" || type === "button" || type === "submit" || type === "reset") return false;
    return true;
}

export function CompanyEdit() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { hasPolicy, roleName, isPropertyRole, propertyId, orgName, user } = useUserRole();

    /** Angular `cancel()`: company-specific roles (manager / admin / regional) go to dashboard, others to companies list. */
    const companyExitPath = (COMPANY_SPECIFIC_ROLES as readonly string[]).includes(roleName ?? "")
        ? "/dashboard"
        : "/company";

    const [activeTab, setActiveTab] = useState<CompanyEditTabId>("companyinfo");
    const [companyDetails, setCompanyDetails] = useState<CompanyEditDetails | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
    const [showExitUnsavedConfirm, setShowExitUnsavedConfirm] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    /** Bump after successful POST /api/company/:id so Liberty notes refetch like Angular loadNotes(). */
    const [internalNotesRefreshKey, setInternalNotesRefreshKey] = useState(0);
    const [duplicateAddressConfirmOpen, setDuplicateAddressConfirmOpen] = useState(false);
    const [duplicateCompanies, setDuplicateCompanies] = useState<DuplicateCompanyRow[]>([]);
    /**
     * Header "Save" → "Save Changes?" → confirm: full-page busy overlay + disabled interaction until
     * `handleUpdateCompany` finishes (or validation blocks submit — cleared via rAF + ref).
     */
    const [isHeaderSaveLoading, setIsHeaderSaveLoading] = useState(false);
    const headerSavePendingRef = useRef(false);
    const headerSaveStartedRef = useRef(false);
    const duplicateDialogRef = useRef<{
        resolve: () => void;
        reject: (e: unknown) => void;
        retryPayload: Record<string, unknown>;
    } | null>(null);

    const endHeaderSaveOverlay = useCallback(() => {
        if (headerSavePendingRef.current) {
            headerSavePendingRef.current = false;
            headerSaveStartedRef.current = false;
            setIsHeaderSaveLoading(false);
        }
    }, []);

    /** Marks header Save as in-flight so the post-submit rAF guard does not clear the overlay early. */
    const beginHeaderSaveWork = useCallback(() => {
        if (headerSavePendingRef.current) {
            headerSaveStartedRef.current = true;
        }
    }, []);

    /** Keep visited tabs mounted (hidden) so react-hook-form state survives tab switches without save. */
    const [mountedCompanyEditTabIds, setMountedCompanyEditTabIds] = useState<Set<CompanyEditTabId>>(() => new Set());
    const prevCompanyIdForTabsRef = useRef<string | undefined>(undefined);

    const activeTabRef = useRef<CompanyEditTabId>(activeTab);
    useEffect(() => {
        activeTabRef.current = activeTab;
    }, [activeTab]);

    /** `true` after user chooses Save from exit flow — completed after company POST (or full Integrations / Config sequence). */
    const exitAfterSaveRef = useRef(false);
    /** Whether the unsaved prompt was opened from in-app navigation (`useBlocker`) vs the Exit button. */
    const exitSourceRef = useRef<"button" | "blocker">("button");

    /**
     * Angular `propertyList()` navigates to the property list/edit with no save prompt.
     * `useBlocker` would otherwise intercept `navigate()` and show the same dialog as Exit — bypass only for that path.
     */
    const [bypassNavigationBlockForProperties, setBypassNavigationBlockForProperties] = useState(false);
    /** Set before intentional leave so `navigate()` is not intercepted by a stale dirty blocker. */
    const allowLeaveWithoutBlockRef = useRef(false);

    useBreadcrumbPageTitle(companyDetails?.company_name ?? null);

    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) =>
            shouldBlockUnsavedNavigation({
                allowLeave: allowLeaveWithoutBlockRef.current,
                bypass: bypassNavigationBlockForProperties,
                hasUnsavedChanges,
                currentPathname: currentLocation.pathname,
                nextPathname: nextLocation.pathname,
            }),
    );

    useEffect(() => {
        if (blocker.state === "blocked") {
            exitSourceRef.current = "blocker";
            setShowExitUnsavedConfirm(true);
        }
    }, [blocker.state]);

    useEffect(() => {
        if (!hasUnsavedChanges) return;
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [hasUnsavedChanges]);

    const handleMainSubmitClick = () => {
        if (isHeaderSaveLoading || showSubmitConfirm || isDeleting) return;
        if (COMPANY_EDIT_TABS_WITHOUT_GLOBAL_FORM.includes(activeTab)) {
            showInfo(getNoGlobalSubmitMessage(activeTab));
            return;
        }
        setShowSubmitConfirm(true);
    };

    const handleSaveChangesDialogConfirm = useCallback(() => {
        setShowSubmitConfirm(false);
        headerSavePendingRef.current = true;
        headerSaveStartedRef.current = false;
        setIsHeaderSaveLoading(true);
        const form = document.querySelector(`form[data-tab-id="${activeTab}"]`);
        if (form instanceof HTMLFormElement) {
            form.requestSubmit();
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (headerSavePendingRef.current && !headerSaveStartedRef.current) {
                        endHeaderSaveOverlay();
                    }
                });
            });
        } else if (COMPANY_EDIT_TABS_WITHOUT_GLOBAL_FORM.includes(activeTab)) {
            endHeaderSaveOverlay();
            showInfo(getNoGlobalSubmitMessage(activeTab));
        } else {
            endHeaderSaveOverlay();
            showError("Nothing to save on this tab. Use the tab's own Save button where available.");
        }
    }, [activeTab, endHeaderSaveOverlay]);

    /**
     * Until `current_role` is hydrated, `hasPolicy` is false for every gated tab, so the filter would
     * leave only Config (no policy) — refresh then wrongly selected Config. Show full tab strip until role is ready.
     */
    const availableTabs = useMemo(() => {
        if (!user?.current_role) return COMPANY_EDIT_TABS;
        return COMPANY_EDIT_TABS.filter((tab) => !tab.policy || hasPolicy([tab.policy]));
    }, [user?.current_role, hasPolicy]);

    const selectTab = useCallback((tabId: CompanyEditTabId) => {
        setActiveTab(tabId);
    }, []);

    /** Honor Angular `#bankinginfo` (and `#payments`) deep-link into Payments tab. */
    useLayoutEffect(() => {
        if (!id) return
        const hash = window.location.hash.replace(/^#/, '').trim().toLowerCase()
        if (hash === 'bankinginfo' || hash === 'payments') {
            setActiveTab('payments')
        }
        if (!window.location.hash) return
        window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`)
    }, [id]);

    /** Angular `setFirstVisibleTab()` order = `TABS` — pick first allowed tab if current is not visible. */
    useLayoutEffect(() => {
        if (!id) return;
        setActiveTab((prev) => {
            const allowedIds = new Set(availableTabs.map((t) => t.id));
            if (allowedIds.has(prev)) return prev;
            return getFirstAllowedCompanyEditTabId(hasPolicy);
        });
    }, [id, availableTabs, hasPolicy]);

    /** After tab routing (hash / first allowed), track which panels stay mounted so forms keep unsaved edits. */
    useLayoutEffect(() => {
        if (!id) return;
        if (prevCompanyIdForTabsRef.current !== id) {
            prevCompanyIdForTabsRef.current = id;
            setMountedCompanyEditTabIds(new Set([activeTab]));
            return;
        }
        setMountedCompanyEditTabIds((prev) => {
            const next = new Set(prev);
            next.add(activeTab);
            return next;
        });
    }, [id, activeTab]);

    useEffect(() => {
        const allowed = new Set(availableTabs.map((t) => t.id));
        if (allowed.has(activeTab)) return;
        setActiveTab((availableTabs[0]?.id ?? "companyinfo") as CompanyEditTabId);
    }, [activeTab, availableTabs]);

    const fetchCompanyDetails = useCallback(
        async (options?: { preserveDirty?: boolean; skipLoading?: boolean }) => {
            if (!id) return;
            const skipLoading = options?.skipLoading === true;
            if (!skipLoading) {
                setIsLoading(true);
            }
            try {
                const response = await apiClient.get<CompanyEditDetails>(`/api/company/${id}`);
                setCompanyDetails((prev) => {
                    const next = response.data as Record<string, unknown>;
                    if (!prev) return response.data;
                    const prevRec = prev as Record<string, unknown>;
                    const prevHasNested =
                        prevRec.company_config != null ||
                        prevRec.companyConfig != null;
                    const nextHasNested =
                        next.company_config != null || next.companyConfig != null;
                    /** Some POST/refetch paths return a slimmer company object without nested config — keep prior tree so Config tab `useEffect` does not wipe fields. */
                    if (prevHasNested && !nextHasNested) {
                        const merged = { ...next };
                        if (prevRec.company_config != null) merged.company_config = prevRec.company_config;
                        if (prevRec.companyConfig != null) merged.companyConfig = prevRec.companyConfig;
                        return merged as CompanyEditDetails;
                    }
                    return response.data;
                });
                if (!options?.preserveDirty) {
                    setHasUnsavedChanges(false);
                }
            } catch (error) {
                showApiError(error);
                navigate("/company");
            } finally {
                if (!skipLoading) {
                    setIsLoading(false);
                }
            }
        },
        [id, navigate],
    );

    useEffect(() => {
        if (id) {
            fetchCompanyDetails();
        }
    }, [id, fetchCompanyDetails]);

    const completeSuccessfulCompanyPost = useCallback(async () => {
        const integrationsLikeTab = activeTabRef.current === "config";
        /** Like `PropertyEdit.refreshPropertyDetails` after save: refetch without `setIsLoading` — full-page spinner feels like a refresh. */
        await fetchCompanyDetails({
            preserveDirty: integrationsLikeTab,
            skipLoading: true,
        });
        setInternalNotesRefreshKey((k) => k + 1);
        if (exitAfterSaveRef.current && !integrationsLikeTab) {
            exitAfterSaveRef.current = false;
            setHasUnsavedChanges(false);
            if (exitSourceRef.current === "blocker") {
                exitSourceRef.current = "button";
                if (blocker.state === "blocked") blocker.proceed();
            } else {
                allowLeaveWithoutBlockRef.current = true;
                navigate(companyExitPath);
            }
        }
    }, [fetchCompanyDetails, blocker, navigate, companyExitPath]);

    const handleUpdateCompany = useCallback(
        async (payload: Record<string, unknown>) => {
            if (!id) return;
            if (headerSavePendingRef.current) {
                headerSaveStartedRef.current = true;
            }
            const tryPost = async (body: Record<string, unknown>) => {
                await postCompanyUpdate(id, body);
                await completeSuccessfulCompanyPost();
            };
            try {
                sendAnalytics('click', 'edit company', '' + id)
                await tryPost(payload);
                endHeaderSaveOverlay();
            } catch (err) {
                const dupes = parseDuplicateCompanyAddressFromApiError(err);
                if (!dupes) {
                    exitAfterSaveRef.current = false;
                    endHeaderSaveOverlay();
                    throw err;
                }
                const retryPayload = { ...payload };
                delete retryPayload.check_exist;
                try {
                    await new Promise<void>((resolve, reject) => {
                        duplicateDialogRef.current = { resolve, reject, retryPayload };
                        setDuplicateCompanies(dupes);
                        setDuplicateAddressConfirmOpen(true);
                    });
                    endHeaderSaveOverlay();
                } catch (e) {
                    exitAfterSaveRef.current = false;
                    endHeaderSaveOverlay();
                    throw e;
                }
            }
        },
        [id, completeSuccessfulCompanyPost, endHeaderSaveOverlay],
    );

    const duplicateAddressHtmlMessage = useMemo(() => {
        if (!duplicateCompanies.length) return "";
        const links = duplicateCompanies
            .map(
                (c) =>
                    `${c.name} - <a data-testid="company-edit-duplicate-company-link-${c.id}" href="/company/edit/${c.id}" target="_blank" rel="noopener noreferrer">${c.id}</a>`,
            )
            .join("<br>");
        return `${links}<br><br>If you have confirmed that there are multiple companies at the same address, then Save and continue.`;
    }, [duplicateCompanies]);

    const handleDuplicateCompanyAddressSave = async () => {
        const ctx = duplicateDialogRef.current;
        if (!ctx || !id) return;
        duplicateDialogRef.current = null;
        setDuplicateAddressConfirmOpen(false);
        try {
            await postCompanyUpdate(id, ctx.retryPayload);
            await completeSuccessfulCompanyPost();
            ctx.resolve();
        } catch (e) {
            ctx.reject(e);
        }
    };

    const handleDuplicateCompanyAddressCancel = () => {
        const ctx = duplicateDialogRef.current;
        duplicateDialogRef.current = null;
        setDuplicateAddressConfirmOpen(false);
        exitAfterSaveRef.current = false;
        ctx?.reject(new CompanyDuplicateAddressCancelledError());
    };

    const clearExitSaveIntent = useCallback(() => {
        exitAfterSaveRef.current = false;
    }, []);

    const markIntegrationsTabSavedClean = useCallback(() => {
        setHasUnsavedChanges(false);
    }, []);

    const completeExitNavigate = useCallback(() => {
        if (!exitAfterSaveRef.current) return;
        exitAfterSaveRef.current = false;
        setHasUnsavedChanges(false);
        if (exitSourceRef.current === "blocker") {
            exitSourceRef.current = "button";
            if (blocker.state === "blocked") blocker.proceed();
        } else {
            exitSourceRef.current = "button";
            allowLeaveWithoutBlockRef.current = true;
            navigate(companyExitPath);
        }
    }, [blocker, navigate, companyExitPath]);

    const handleStayOnPage = useCallback(() => {
        setShowExitUnsavedConfirm(false);
        if (blocker.state === "blocked") {
            blocker.reset();
        }
        exitSourceRef.current = "button";
    }, [blocker]);

    const handleExitWithoutSaving = useCallback(() => {
        setShowExitUnsavedConfirm(false);
        setHasUnsavedChanges(false);
        exitAfterSaveRef.current = false;
        if (exitSourceRef.current === "blocker") {
            exitSourceRef.current = "button";
            if (blocker.state === "blocked") blocker.proceed();
        } else {
            exitSourceRef.current = "button";
            allowLeaveWithoutBlockRef.current = true;
            navigate(companyExitPath);
        }
    }, [blocker, navigate, companyExitPath]);

    const handleSaveFromExitDialog = useCallback(() => {
        setShowExitUnsavedConfirm(false);
        const cannotUseHeaderSave = COMPANY_EDIT_TABS_WITHOUT_GLOBAL_FORM.includes(activeTab);
        if (cannotUseHeaderSave) {
            showInfo(getNoGlobalSubmitMessage(activeTab));
            exitAfterSaveRef.current = false;
            if (blocker.state === "blocked") blocker.reset();
            exitSourceRef.current = "button";
            return;
        }
        exitAfterSaveRef.current = true;
        headerSavePendingRef.current = true;
        headerSaveStartedRef.current = false;
        setIsHeaderSaveLoading(true);
        const form = document.querySelector(`form[data-tab-id="${activeTab}"]`);
        if (form instanceof HTMLFormElement) {
            form.requestSubmit();
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (headerSavePendingRef.current && !headerSaveStartedRef.current) {
                        endHeaderSaveOverlay();
                        exitAfterSaveRef.current = false;
                    }
                });
            });
        } else {
            exitAfterSaveRef.current = false;
            endHeaderSaveOverlay();
            showError("Nothing to save on this tab. Use the tab's own Save button where available.");
            if (blocker.state === "blocked") blocker.reset();
            exitSourceRef.current = "button";
        }
    }, [activeTab, blocker, endHeaderSaveOverlay]);

    const requestLeaveCompanyEdit = useCallback(() => {
        if (
            isHeaderSaveLoading ||
            isDeleting ||
            showSubmitConfirm ||
            showExitUnsavedConfirm ||
            showDeleteConfirm ||
            duplicateAddressConfirmOpen
        ) {
            return;
        }
        if (!hasUnsavedChanges) {
            navigate(companyExitPath);
            return;
        }
        exitSourceRef.current = "button";
        setShowExitUnsavedConfirm(true);
    }, [
        hasUnsavedChanges,
        navigate,
        companyExitPath,
        isHeaderSaveLoading,
        isDeleting,
        showSubmitConfirm,
        showExitUnsavedConfirm,
        showDeleteConfirm,
        duplicateAddressConfirmOpen,
    ]);

    /** Angular `propertyList()` + `redirecttoProperty()` — session filter then list vs property edit for property roles. */
    const navigateToProperties = useCallback(() => {
        if (
            isHeaderSaveLoading ||
            isDeleting ||
            showSubmitConfirm ||
            showExitUnsavedConfirm ||
            showDeleteConfirm ||
            duplicateAddressConfirmOpen
        ) {
            return;
        }
        if (companyDetails && id) {
            try {
                sessionStorage.setItem(
                    PROPERTYLIST_FILTER_SESSION_KEY,
                    JSON.stringify({
                        company_id: Number(id),
                        company_name: companyDetails.company_name,
                        org_id: companyDetails.org_id,
                        org_name: orgName ?? "",
                    }),
                );
            } catch {
            }
        }
        flushSync(() => setBypassNavigationBlockForProperties(true));
        if (isPropertyRole && propertyId != null) {
            navigate(`/property/edit/${propertyId}`);
        } else {
            navigate("/property");
        }
    }, [
        companyDetails,
        id,
        orgName,
        isPropertyRole,
        propertyId,
        navigate,
        isHeaderSaveLoading,
        isDeleting,
        showSubmitConfirm,
        showExitUnsavedConfirm,
        showDeleteConfirm,
        duplicateAddressConfirmOpen,
    ]);

    // Angular template/saveFavorite: `favorite == 1` (API may return 1 or "1").
    const isFavorite =
        companyDetails?.favorite === 1 ||
        companyDetails?.favorite === "1" ||
        companyDetails?.favorite === true;

    const handleToggleFavorite = async () => {
        if (
            isHeaderSaveLoading ||
            isDeleting ||
            showSubmitConfirm ||
            showExitUnsavedConfirm ||
            showDeleteConfirm ||
            duplicateAddressConfirmOpen
        ) {
            return;
        }
        try {
            await apiClient.post(`/api/company_favorite/${id}`);
            setCompanyDetails((prev) => {
                if (!prev) return prev;
                const wasFavorite =
                    prev.favorite === 1 || prev.favorite === "1" || prev.favorite === true;
                return { ...prev, favorite: wasFavorite ? 0 : 1 };
            });
        } catch (error) {
            showApiError(error);
        }
    };

    // Delete company — matches Angular deleteCompany()
    const handleDeleteCompany = async () => {
        setShowDeleteConfirm(false);
        setIsDeleting(true);
        try {
            await apiClient.delete(`/api/company/${id}`);
            showSuccess('Company Deleted Successfully!');
            allowLeaveWithoutBlockRef.current = true;
            navigate('/company');
        } catch (error: unknown) {
            showApiError(error);
        } finally {
            setIsDeleting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex h-64 items-center justify-center" data-testid="company-edit-loading">
                <Loader2 className="h-8 w-8 animate-spin text-[rgb(21_151_255)]" />
            </div>
        );
    }

    if (!companyDetails) {
        return null; // Will redirect in useEffect catch
    }

    /** Disable all header actions while save/delete runs or a blocking confirm is open (avoids double-submit / race). */
    const headerActionsDisabled =
        isHeaderSaveLoading ||
        isDeleting ||
        showSubmitConfirm ||
        showExitUnsavedConfirm ||
        showDeleteConfirm ||
        duplicateAddressConfirmOpen;

    const companyRelatedLinks: EntityEditRelatedLink[] = [
        {
            key: "properties",
            label: "Properties",
            icon: Building2,
            testId: "company-edit-properties-button",
            onClick: () => {
                if (headerActionsDisabled) return;
                navigateToProperties();
            },
        },
    ];
    if (hasPolicy(["transfer_company"])) {
        companyRelatedLinks.push({
            key: "transfer",
            label: "Transfer",
            icon: ArrowLeftRight,
            testId: "company-edit-transfer-button",
            onClick: () => {
                if (headerActionsDisabled) return;
                openTransferWizardInNewTab(String(id ?? ""), "transfer_company");
            },
        });
    }
    companyRelatedLinks.push(
        {
            key: "residents",
            label: "Residents",
            icon: Users,
            testId: "company-edit-residents-button",
            onClick: () => {
                if (headerActionsDisabled) return;
                sessionStorage.setItem(
                    "residentlist_filter",
                    JSON.stringify({
                        companyId: String(id ?? ""),
                        company_name: String(companyDetails.company_name ?? ""),
                        past_customer: "1",
                    }),
                );
                navigate("/resident");
            },
        },
        {
            key: "reimbursements",
            label: "Reimbursements",
            icon: Receipt,
            testId: "company-edit-reimbursements-button",
            onClick: () => {
                if (headerActionsDisabled) return;
                sessionStorage.setItem(
                    "defaultlist_filter",
                    JSON.stringify({
                        __source: "reimbursements_react_v2",
                        companyId: String(id ?? ""),
                        company_name: String(companyDetails.company_name ?? ""),
                        past_customer: "1",
                        org_id: companyDetails.org_id || "",
                    }),
                );
                navigate("/defaults");
            },
        },
    );

    return (
        <div
            className="w-full bg-slate-50/50 min-h-screen"
            data-company-edit-page
            data-testid="company-edit-page"
            style={{ padding: 0, margin: 0 }}
        >
            <PageHeader
              title="Edit Company"
              testIdRoot="company-edit-page-header"
              impersonationStopDataTestId="company-edit-stop-impersonate"
            />

            <EntityEditHeaderCard
                lead={
                    <>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <h2 className="text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">
                                {companyDetails.company_name}
                            </h2>
                            {["ROLE_LIBERTY_SALES", "ROLE_ADMIN"].includes(roleName ?? "") && (
                                <button
                                    type="button"
                                    data-testid="company-edit-favorite-toggle"
                                    disabled={headerActionsDisabled}
                                    onClick={handleToggleFavorite}
                                    className="inline-flex shrink-0 rounded-full p-1.5 text-red-500 transition-colors hover:bg-red-50 disabled:pointer-events-none disabled:opacity-50"
                                    title={isFavorite ? "Remove from favorites" : "Add to favorites"}
                                >
                                    <Heart
                                        size={22}
                                        color="currentColor"
                                        fill={isFavorite ? "currentColor" : "none"}
                                    />
                                </button>
                            )}
                        </div>
                        <p className="mt-1 text-sm text-slate-500">
                            Company ID <span className="font-medium tabular-nums text-slate-700">{id}</span>
                        </p>
                    </>
                }
                toolbar={
                    <EntityEditHeaderToolbar
                        headerActionsDisabled={headerActionsDisabled}
                        isHeaderSaveLoading={isHeaderSaveLoading}
                        isDeleting={isDeleting}
                        canEdit={hasPolicy(["company_info_edit"])}
                        canDelete={hasPolicy(["company_delete"])}
                        relatedLinks={companyRelatedLinks}
                        save={{ type: "button", onClick: handleMainSubmitClick, testId: "company-edit-save-button" }}
                        cancelTestId="company-edit-cancel-button"
                        deleteTestId="company-edit-delete-button"
                        onCancel={requestLeaveCompanyEdit}
                        onDelete={() => {
                            if (headerActionsDisabled) return
                            setShowDeleteConfirm(true)
                        }}
                    />
                }
            />

            {/* ── Tabs Card ── */}
            <div className="mx-4 my-4 sm:mx-6">
                <div className="bg-white shadow-sm border border-slate-200 overflow-hidden rounded-lg">
                    <EntityEditTabBar
                        navTestId="company-edit-tab-nav"
                        tabs={availableTabs.map((tab) => ({
                            id: tab.id,
                            label: tab.label,
                            testId: `company-edit-tab-${tab.id}`,
                        }))}
                        activeTab={activeTab}
                        onSelect={(tabId) => selectTab(tabId as CompanyEditTabId)}
                    />

                    <div
                        className="p-4 sm:p-6 bg-slate-50/50 min-h-[500px]"
                        onInputCapture={(e) => {
                            if (isFormControlTargetForDirtyMark(e.target)) setHasUnsavedChanges(true);
                        }}
                        onChangeCapture={(e) => {
                            if (isFormControlTargetForDirtyMark(e.target)) setHasUnsavedChanges(true);
                        }}
                    >
                        {mountedCompanyEditTabIds.has("companyinfo") && (
                            <div role="tabpanel" hidden={activeTab !== "companyinfo"} aria-hidden={activeTab !== "companyinfo"} className={activeTab !== "companyinfo" ? "hidden" : undefined}>
                                <CompanyInfoTab
                                    key={id}
                                    company={companyDetails}
                                    companyId={id}
                                    onUpdate={handleUpdateCompany}
                                    onRefresh={fetchCompanyDetails}
                                />
                            </div>
                        )}
                        {mountedCompanyEditTabIds.has("executivesummary") && (
                            <div role="tabpanel" hidden={activeTab !== "executivesummary"} aria-hidden={activeTab !== "executivesummary"} className={activeTab !== "executivesummary" ? "hidden" : undefined}>
                                <ExecutiveSummaryTab key={id} company={companyDetails} companyId={id} onUpdate={handleUpdateCompany} />
                            </div>
                        )}
                        {mountedCompanyEditTabIds.has("contacts") && (
                            <div role="tabpanel" hidden={activeTab !== "contacts"} aria-hidden={activeTab !== "contacts"} className={activeTab !== "contacts" ? "hidden" : undefined}>
                                <ContactsTab key={id} company={companyDetails} companyId={id} onUpdate={handleUpdateCompany} />
                            </div>
                        )}
                        {mountedCompanyEditTabIds.has("preferences") && (
                            <div role="tabpanel" hidden={activeTab !== "preferences"} aria-hidden={activeTab !== "preferences"} className={activeTab !== "preferences" ? "hidden" : undefined}>
                                <PreferencesTab key={id} company={companyDetails} onUpdate={handleUpdateCompany} />
                            </div>
                        )}
                        {mountedCompanyEditTabIds.has("communications") && (
                            <div role="tabpanel" hidden={activeTab !== "communications"} aria-hidden={activeTab !== "communications"} className={activeTab !== "communications" ? "hidden" : undefined}>
                                <CommunicationsTab
                                    key={id}
                                    company={companyDetails}
                                    companyId={id}
                                    onUpdate={handleUpdateCompany}
                                />
                            </div>
                        )}
                        {mountedCompanyEditTabIds.has("payments") && (
                            <div role="tabpanel" hidden={activeTab !== "payments"} aria-hidden={activeTab !== "payments"} className={activeTab !== "payments" ? "hidden" : undefined}>
                                <PaymentsTab
                                    key={id}
                                    company={companyDetails}
                                    companyId={id}
                                    onUpdate={handleUpdateCompany}
                                    onCompanyRefresh={() => fetchCompanyDetails({ skipLoading: true })}
                                />
                            </div>
                        )}
                        {mountedCompanyEditTabIds.has("integrations") && (
                            <div role="tabpanel" hidden={activeTab !== "integrations"} aria-hidden={activeTab !== "integrations"} className={activeTab !== "integrations" ? "hidden" : undefined}>
                                <IntegrationsTab
                                    key={id}
                                    company={companyDetails}
                                    companyId={id || ""}
                                    onUpdate={handleUpdateCompany}
                                    clearExitSaveIntent={clearExitSaveIntent}
                                    completeExitNavigate={completeExitNavigate}
                                    markIntegrationsTabSavedClean={markIntegrationsTabSavedClean}
                                />
                            </div>
                        )}
                        {mountedCompanyEditTabIds.has("activity") && (
                            <div role="tabpanel" hidden={activeTab !== "activity"} aria-hidden={activeTab !== "activity"} className={activeTab !== "activity" ? "hidden" : undefined}>
                                <ActivityTab key={id} companyId={id || ""} company={companyDetails} onUpdate={handleUpdateCompany} />
                            </div>
                        )}
                        {mountedCompanyEditTabIds.has("config") && (
                            <div role="tabpanel" hidden={activeTab !== "config"} aria-hidden={activeTab !== "config"} className={activeTab !== "config" ? "hidden" : undefined}>
                                <CompanyConfigTab
                                    key={id}
                                    company={companyDetails}
                                    companyId={id || ""}
                                    onUpdate={handleUpdateCompany}
                                    beginHeaderSaveWork={beginHeaderSaveWork}
                                    endHeaderSaveOverlay={endHeaderSaveOverlay}
                                    clearExitSaveIntent={clearExitSaveIntent}
                                    completeExitNavigate={completeExitNavigate}
                                    markIntegrationsTabSavedClean={markIntegrationsTabSavedClean}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Liberty Internal Notes – visible on all tabs (matches Angular company-edit) */}
            {hasPolicy(["company_notes_view"]) && companyDetails && (
                <LibertyInternalNotes
                    fromPage="COMPANY"
                    entityId={id || ''}
                    canEdit={hasPolicy(["company_notes_edit"])}
                    refreshKey={internalNotesRefreshKey}
                />
            )}

            {/* Delete Confirmation Modal */}
            <ConfirmDialog
                isOpen={showDeleteConfirm}
                title="Confirm"
                message="Delete Company?"
                primaryLabel="Yes"
                onPrimary={handleDeleteCompany}
                secondaryLabel="No"
                onSecondary={() => setShowDeleteConfirm(false)}
                {...companyEditConfirmPresentation}
                overlayDataTestId="company-edit-delete-confirm-overlay"
                primaryButtonDataTestId="company-edit-delete-confirm-yes-button"
                secondaryButtonDataTestId="company-edit-delete-confirm-no-button"
                closeButtonDataTestId="company-edit-delete-confirm-close-button"
            />

            {/* Save / Save Changes confirmation — triggers the active tab's form submit */}
            <ConfirmDialog
                isOpen={showSubmitConfirm}
                title="Save Changes?"
                message="You have made changes. Are you sure you want to save them?"
                primaryLabel="Save"
                onPrimary={handleSaveChangesDialogConfirm}
                secondaryLabel="Cancel"
                onSecondary={() => setShowSubmitConfirm(false)}
                {...companyEditConfirmPresentation}
                overlayDataTestId="company-edit-save-changes-confirm-overlay"
                primaryButtonDataTestId="company-edit-save-changes-confirm-save-button"
                secondaryButtonDataTestId="company-edit-save-changes-confirm-cancel-button"
            />

            <ConfirmDialog
                isOpen={showExitUnsavedConfirm}
                title="Save changes?"
                message="You have unsaved changes. Save before leaving?"
                primaryLabel="Save"
                onPrimary={handleSaveFromExitDialog}
                secondaryLabel="No"
                onSecondary={handleExitWithoutSaving}
                onDismiss={handleStayOnPage}
                {...companyEditConfirmPresentation}
                overlayDataTestId="company-edit-exit-unsaved-confirm-overlay"
                primaryButtonDataTestId="company-edit-exit-unsaved-confirm-save-button"
                secondaryButtonDataTestId="company-edit-exit-unsaved-confirm-no-button"
                closeButtonDataTestId="company-edit-exit-unsaved-confirm-close-button"
            />

            <ConfirmDialog
                isOpen={duplicateAddressConfirmOpen}
                title="Another company exists at this address"
                message=""
                htmlMessage={duplicateAddressHtmlMessage}
                primaryLabel="Save"
                onPrimary={() => {
                    void handleDuplicateCompanyAddressSave();
                }}
                secondaryLabel="Cancel"
                onSecondary={handleDuplicateCompanyAddressCancel}
                onDismiss={handleDuplicateCompanyAddressCancel}
                {...companyEditConfirmPresentation}
                overlayDataTestId="company-edit-duplicate-address-confirm-overlay"
                primaryButtonDataTestId="company-edit-duplicate-address-confirm-save-button"
                secondaryButtonDataTestId="company-edit-duplicate-address-confirm-cancel-button"
                closeButtonDataTestId="company-edit-duplicate-address-confirm-close-button"
            />

            {isHeaderSaveLoading && (
                <div
                    className="pointer-events-auto fixed inset-0 z-[10000] flex items-center justify-center bg-transparent"
                    data-testid="company-edit-header-save-loading-overlay"
                    role="status"
                    aria-live="polite"
                    aria-busy="true"
                >
                    <Loader2
                        className="h-10 w-10 animate-spin text-[rgb(21_151_255)]"
                        aria-label="Saving"
                    />
                </div>
            )}
        </div>
    );
}
